/**
 * Hooks do dono (área /merchant owner). Endpoints verificados no backend:
 *   GET  /public/merchants/{id}                 → detalhe (MerchantResponse)
 *   GET  /merchants/{id}/products?size=200…      → lista completa (rich shape)
 *   GET  /merchants/{id}/products/{pid}          → detalhe produto
 *   GET  /merchants/{id}/categories              → lista categorias
 *   GET  /merchants/{id}/operating-hours         → horário
 *   PUT  /merchants/{id}/operating-hours         → salvar horário (full replace)
 *   PATCH /merchants/{id}/accepting-orders {accepting} → 204 (toggle)
 *   DELETE /merchants/{id}/open-override         → 204 (limpar força manual)
 *   PUT  /merchants/{id} {logoUrl}               → salvar logo (Fix B; partial)
 *   POST /merchants/{id}/products                → criar produto
 *   PUT  /merchants/{id}/products/{pid}          → atualizar produto
 *   PATCH /merchants/{id}/products/{pid}/featured → toggle destaque (sem corpo)
 *   DELETE /merchants/{id}/products/{pid}        → excluir produto
 *   POST /merchants/{id}/categories              → criar categoria
 *   PATCH /merchants/{id}/categories/{cid}       → atualizar categoria
 *   DELETE /merchants/{id}/categories/{cid}      → excluir categoria
 *   PATCH /merchants/{id}/categories/reorder {ids} → reordenar
 *   POST /products/{pid}/option-groups           → criar grupo de opcionais
 *   PUT  /products/{pid}/option-groups/{gid}     → atualizar grupo
 *   DELETE /products/{pid}/option-groups/{gid}   → excluir grupo
 *   POST /option-groups/{gid}/options            → criar opção
 *   PUT  /option-groups/{gid}/options/{oid}      → atualizar opção
 *   DELETE /option-groups/{gid}/options/{oid}    → excluir opção
 *   GET  /public/allergens                       → alérgenos (sem auth)
 *   GET  /merchants/{id}/anticipation-options      → opções de antecipação (MER-19)
 *   PUT  /merchants/{id}/anticipation-choice {days} → escolher antecipação (OWNER-only)
 *   GET  /merchants/{id}/courier-pool              → pool de frota própria (MER-20, OWNER-only)
 *   POST /merchants/{id}/courier-pool {email}      → convidar entregador (OWNER-only)
 *   DELETE /merchants/{id}/courier-pool/{courierId} → remover do pool (OWNER-only)
 *   GET  /merchants/{id}/reviews/all               → avaliações (todos os status, MER-22)
 *   PUT  /merchants/{id}/reviews/{reviewId}/reply   → responder avaliação
 *   GET  /public/merchants/{id}/reviews/summary     → média + contagem (sem auth)
 * Tipos hand-modeled (api.d.ts é stale).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { merchantApiFetch, toQuery, compact } from '@favelafood/shared/api/fetcher';
import type { ActivePromotionView } from './promotions';
import { getMyMerchants } from '../kds/api';

const API = '/api/v1';

export type MerchantStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'PENDING_OWNER_ACTIVATION';
export type DispatchMode = 'OWN_COURIERS' | 'GLOBAL_POOL';

export interface OwnerMerchant {
  id: string;
  name: string;
  category: string | null;
  categoryId: string | null;
  description: string | null;
  phone: string | null;
  logoUrl: string | null;
  /** Capa/banner da vitrine (FAV-147). */
  coverUrl?: string | null;
  status: MerchantStatus;
  isAcceptingOrders: boolean;
  autoAcceptOrders?: boolean;
  isOpen: boolean;
  dispatchMode: DispatchMode;
  minimumOrderAmount: number | null;
  estimatedPrepMinutes: number | null;
  /** Frota própria (BACK-157) — quando true, entregas são ofertadas primeiro ao pool 2. */
  ownFleetEnabled?: boolean;
  // Endereço da loja — o detail público (MerchantDetailResponse) já expõe (FAV-140-loja).
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

/** `BillingInfoResponse.java` (FAV-148) — razão social, CNPJ e PIX (decriptada) do dono. */
export interface BillingInfo {
  legalName?: string | null;
  cnpj?: string | null;
  pixKey?: string | null;
  pixKeyType?: string | null;
}

/** Campos operacionais editáveis pelo dono via PUT /merchants/{id} (lazy update). */
export interface OwnerMerchantUpdate {
  name?: string;
  description?: string;
  phone?: string;
  minimumOrderAmount?: number;
  estimatedPrepMinutes?: number;
  autoAcceptOrders?: boolean;
  dispatchMode?: DispatchMode;
  coverUrl?: string;
  ownFleetEnabled?: boolean;
}

/* ---- Catálogo (Fase 3b) ---- */
export interface OwnerAllergen {
  id: string;
  name: string;
}

export interface OwnerOption {
  id?: string;
  name: string;
  priceModifier: number;
  isAvailable: boolean;
  displayOrder: number;
  /** MER-23: texto curto explicando a opção (ex.: "sem lactose"). Max 255. */
  description?: string | null | undefined;
}

export interface OwnerOptionGroup {
  id?: string;
  name: string;
  minSelections: number;
  maxSelections: number;
  isRequired: boolean;
  displayOrder: number;
  options: OwnerOption[];
}

/**
 * Shape rico de produto — usado em lista (3b) e detalhe (editor, opcionais).
 * Contém .available/.isFeatured que os KPIs da 3a usam.
 */
export interface OwnerProductDetail {
  id: string;
  merchantId: string;
  name: string;
  description: string | null;
  merchantCategoryId: string | null;
  price: number;
  imageUrl: string | null;
  isActive: boolean;
  isFeatured: boolean;
  preparationTimeMinutes: number | null;
  stockQuantity: number | null;
  tags: string[];
  allergens: OwnerAllergen[];
  optionGroups: OwnerOptionGroup[];
  computed?: { available?: boolean } | null;
  /** Computed availability — shorthand for computed?.available ?? isActive */
  available: boolean;
  activePromotion?: ActivePromotionView | null;
}

/**
 * @deprecated Use OwnerProductDetail instead (shape estendido para 3b).
 * Mantido como alias para retrocompatibilidade com código 3a que importa OwnerProduct.
 */
export type OwnerProduct = OwnerProductDetail;

export interface OwnerCategory {
  id: string;
  name: string;
  position: number;
  iconUrl?: string | null;
  isActive?: boolean;
}

export interface CreateProductBody {
  name: string;
  description?: string | undefined;
  merchantCategoryId: string;
  price: number;
  imageUrl?: string | undefined;
  preparationTimeMinutes?: number | undefined;
  stockQuantity?: number | undefined;
  tags?: string[] | undefined;
  allergenIds?: string[] | undefined;
}

export interface UpdateProductBody extends Partial<CreateProductBody> {
  isActive?: boolean | undefined;
  isFeatured?: boolean | undefined;
}

export interface OwnerCategoryWrite {
  name: string;
  iconUrl?: string | null | undefined;
  isActive?: boolean | undefined;
  position?: number | undefined;
}

export interface OperatingHours {
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

/* ---- PIX payout key (FAV-172 / FAV-113) ---- */

export type PixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'RANDOM';

/** Body of `PUT /api/v1/merchants/me/pix-key` (PixKeyUpdateRequest). */
export interface PixKeyUpdateBody {
  pixKey: string;
  pixKeyType: PixKeyType;
}

/** Response of the PIX-key update — LGPD: only the masked key + type. */
export interface PixKeyResponse {
  pixKeyMasked: string;
  pixKeyType: PixKeyType;
}

interface RawProduct {
  id: string;
  merchantId?: string;
  name?: string;
  description?: string | null;
  merchantCategoryId?: string | null;
  price?: number;
  imageUrl?: string | null;
  isActive: boolean;
  isFeatured: boolean;
  preparationTimeMinutes?: number | null;
  stockQuantity?: number | null;
  tags?: string[];
  allergens?: OwnerAllergen[];
  optionGroups?: OwnerOptionGroup[];
  computed?: { available?: boolean } | null;
  activePromotion?: ActivePromotionView | null;
}

interface Paged<T> {
  content: T[];
  /** Não-opcional de propósito: PageResponse.totalPages é `int` no backend, sempre vem. */
  totalPages: number;
}

/** Teto de páginas varridas por `fetchAllPages` — 50 × 100 = 5.000, o mesmo teto do parser .xlsx. */
const MAX_PAGES = 50;
/** Tamanho de página efetivo: o servidor grampeia `?size` em 100 (FF-SEC-016). */
const PAGE_SIZE = 100;

/**
 * Varre uma listagem paginada até a última página.
 *
 * O servidor grampeia `?size` em 100 silenciosamente (`spring.data.web.pageable.max-page-size`,
 * FF-SEC-016) — pedir `size=5000` devolve 100 sem erro nenhum. Quem quer o conjunto inteiro
 * precisa paginar.
 *
 * `sort` é obrigatório: as queries de catálogo não têm `ORDER BY` e no Postgres LIMIT/OFFSET sem
 * ordem pode repetir e pular linhas entre páginas — linha pulada vira produto duplicado na
 * reimportação do catálogo, que é exatamente o bug que isto conserta.
 */
async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<Paged<T>>,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const res = await fetchPage(page);
    all.push(...res.content);
    if (page + 1 >= res.totalPages) break;
  }
  return all;
}

const mapProduct = (p: RawProduct): OwnerProductDetail => ({
  id: p.id,
  merchantId: p.merchantId ?? '',
  name: p.name ?? '',
  description: p.description ?? null,
  merchantCategoryId: p.merchantCategoryId ?? null,
  price: p.price ?? 0,
  imageUrl: p.imageUrl ?? null,
  isActive: p.isActive,
  isFeatured: p.isFeatured,
  preparationTimeMinutes: p.preparationTimeMinutes ?? null,
  stockQuantity: p.stockQuantity ?? null,
  tags: p.tags ?? [],
  allergens: p.allergens ?? [],
  optionGroups: p.optionGroups ?? [],
  computed: p.computed ?? null,
  available: p.computed?.available ?? p.isActive,
  activePromotion: p.activePromotion ?? null,
});

export const ownerKeys = {
  all: ['owner'] as const,
  merchant: (id: string) => ['owner', 'merchant', id] as const,
  productsAll: ['owner', 'products'] as const,
  products: (id: string) => ['owner', 'products', id] as const,
  product: (id: string, pid: string) => ['owner', 'product', id, pid] as const,
  categories: (id: string) => ['owner', 'categories', id] as const,
  hours: (id: string) => ['owner', 'hours', id] as const,
};

export function useOwnerMerchant(id: string | null): UseQueryResult<OwnerMerchant> {
  return useQuery({
    queryKey: ownerKeys.merchant(id ?? ''),
    enabled: !!id,
    queryFn: () => merchantApiFetch<OwnerMerchant>(`${API}/public/merchants/${id}`),
  });
}

export function useOwnerProducts(id: string | null): UseQueryResult<OwnerProductDetail[]> {
  return useQuery({
    queryKey: ownerKeys.products(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const raw = await fetchAllPages<RawProduct>((page) => {
        const q = toQuery(
          compact([
            ['page', page],
            ['size', PAGE_SIZE],
            ['sort', 'id,asc'],
            ['includeInactive', true],
          ]),
        );
        return merchantApiFetch<Paged<RawProduct>>(`${API}/merchants/${id}/products${q}`);
      });
      return raw.map(mapProduct);
    },
    // Catálogo grande custa até 50 GETs. As 5 telas que consomem este hook (catálogo, import,
    // resumo, categorias, promoções) compartilham o mesmo cache, então navegar entre elas não
    // repete a varredura. Espelha o TTL do cache `merchant-products` do backend; as mutations
    // de produto e de promoção invalidam esta key, então escrita continua refletindo na hora.
    staleTime: 5 * 60_000,
  });
}

export function useOwnerCategories(id: string | null): UseQueryResult<OwnerCategory[]> {
  return useQuery({
    queryKey: ownerKeys.categories(id ?? ''),
    enabled: !!id,
    queryFn: () =>
      merchantApiFetch<OwnerCategory[]>(`${API}/merchants/${id}/categories`),
  });
}

export function useOwnerOperatingHours(id: string | null): UseQueryResult<OperatingHours[]> {
  return useQuery({
    queryKey: ownerKeys.hours(id ?? ''),
    enabled: !!id,
    queryFn: () =>
      merchantApiFetch<OperatingHours[]>(`${API}/merchants/${id}/operating-hours`),
  });
}

export function useToggleAccepting(id: string): UseMutationResult<void, unknown, boolean> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accepting: boolean) =>
      merchantApiFetch<void>(`${API}/merchants/${id}/accepting-orders`, {
        method: 'PATCH',
        body: { accepting },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.merchant(id) });
    },
  });
}

export function useSaveOperatingHours(
  id: string,
): UseMutationResult<OperatingHours[], unknown, OperatingHours[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: OperatingHours[]) =>
      merchantApiFetch<OperatingHours[]>(`${API}/merchants/${id}/operating-hours`, {
        method: 'PUT',
        body: rows,
      }),
    onSuccess: (data) => {
      qc.setQueryData(ownerKeys.hours(id), data);
    },
  });
}

/**
 * Updates the caller's merchant PIX payout key (FAV-172). The endpoint resolves
 * the merchant from the JWT (`/me/pix-key`), so no id goes in the path; `id` is
 * kept only to invalidate the cached merchant. Response carries the masked key.
 * Endpoint is absent from the stale `api.d.ts` → types are hand-modeled above.
 */
export function useUpdatePixKey(
  id: string,
): UseMutationResult<PixKeyResponse, unknown, PixKeyUpdateBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PixKeyUpdateBody) =>
      merchantApiFetch<PixKeyResponse>(`${API}/merchants/me/pix-key`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.merchant(id) });
    },
  });
}

export function useUpdateMerchantLogo(id: string): UseMutationResult<OwnerMerchant, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (logoUrl: string) =>
      merchantApiFetch<OwnerMerchant>(`${API}/merchants/${id}`, {
        method: 'PUT',
        body: { logoUrl },
      }),
    onSuccess: (data) => {
      qc.setQueryData(ownerKeys.merchant(id), data);
    },
  });
}

export function useClearOpenOverride(id: string): UseMutationResult<void, unknown, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      merchantApiFetch<void>(`${API}/merchants/${id}/open-override`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.merchant(id) });
    },
  });
}

/* ---- Catálogo: Produtos (Fase 3b) ---- */

export function useOwnerProduct(
  id: string,
  pid: string | null,
): UseQueryResult<OwnerProductDetail> {
  return useQuery({
    queryKey: ownerKeys.product(id, pid ?? ''),
    enabled: !!pid,
    queryFn: () =>
      merchantApiFetch<OwnerProductDetail>(`${API}/merchants/${id}/products/${pid}`),
  });
}

/**
 * `silent` suprime a invalidação do catálogo no onSuccess.
 *
 * Existe para laços: a lista de produtos é lida com uma varredura de até 50 páginas
 * (`fetchAllPages`), e o diálogo de importação mantém esse observer ATIVO enquanto roda. Sem o
 * silent, cada linha importada invalidaria a key e dispararia uma varredura inteira — 1.490 linhas
 * × 15 páginas ≈ 22 mil GETs de fundo, o suficiente para bater no rate limit da nginx.
 * Quem passa `silent` fica responsável por invalidar uma vez ao terminar.
 */
export interface MutationScopeOptions {
  silent?: boolean;
}

export function useCreateOwnerProduct(
  id: string,
  opts: MutationScopeOptions = {},
): UseMutationResult<OwnerProductDetail, unknown, CreateProductBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      merchantApiFetch<OwnerProductDetail>(`${API}/merchants/${id}/products`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      if (opts.silent) return;
      void qc.invalidateQueries({ queryKey: ownerKeys.products(id) });
    },
  });
}

export function useUpdateOwnerProduct(
  id: string,
  opts: MutationScopeOptions = {},
): UseMutationResult<OwnerProductDetail, unknown, { pid: string; body: UpdateProductBody }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pid, body }) =>
      merchantApiFetch<OwnerProductDetail>(`${API}/merchants/${id}/products/${pid}`, {
        method: 'PUT',
        body,
      }),
    onSuccess: (_d, { pid }) => {
      if (opts.silent) return;
      void qc.invalidateQueries({ queryKey: ownerKeys.products(id) });
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

export function useToggleOwnerFeatured(
  id: string,
): UseMutationResult<OwnerProductDetail, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pid) =>
      merchantApiFetch<OwnerProductDetail>(
        `${API}/merchants/${id}/products/${pid}/featured`,
        { method: 'PATCH' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.products(id) });
    },
  });
}

export function useDeleteOwnerProduct(
  id: string,
): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pid) =>
      merchantApiFetch<void>(`${API}/merchants/${id}/products/${pid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.products(id) });
    },
  });
}

/* ---- Catálogo: Categorias (Fase 3b) ---- */

export function useCreateOwnerCategory(
  id: string,
): UseMutationResult<OwnerCategory, unknown, OwnerCategoryWrite> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      merchantApiFetch<OwnerCategory>(`${API}/merchants/${id}/categories`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.categories(id) });
    },
  });
}

export function useUpdateOwnerCategory(
  id: string,
): UseMutationResult<OwnerCategory, unknown, { cid: string; body: OwnerCategoryWrite }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cid, body }) =>
      merchantApiFetch<OwnerCategory>(`${API}/merchants/${id}/categories/${cid}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.categories(id) });
    },
  });
}

export function useDeleteOwnerCategory(
  id: string,
): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cid) =>
      merchantApiFetch<void>(`${API}/merchants/${id}/categories/${cid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.categories(id) });
    },
  });
}

export function useReorderOwnerCategories(
  id: string,
): UseMutationResult<OwnerCategory[], unknown, string[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) =>
      merchantApiFetch<OwnerCategory[]>(
        `${API}/merchants/${id}/categories/reorder`,
        { method: 'PATCH', body: { ids } },
      ),
    onSuccess: (data) => {
      qc.setQueryData(ownerKeys.categories(id), data);
    },
  });
}

/* ---- Catálogo: Opcionais — grupos (Fase 3b) ---- */

export function useCreateOptionGroup(
  id: string,
  pid: string,
): UseMutationResult<
  OwnerOptionGroup,
  unknown,
  Omit<OwnerOptionGroup, 'id' | 'options'>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      merchantApiFetch<OwnerOptionGroup>(`${API}/products/${pid}/option-groups`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

export function useUpdateOptionGroup(
  id: string,
  pid: string,
): UseMutationResult<
  OwnerOptionGroup,
  unknown,
  { gid: string; body: Partial<OwnerOptionGroup> }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ gid, body }) =>
      merchantApiFetch<OwnerOptionGroup>(
        `${API}/products/${pid}/option-groups/${gid}`,
        { method: 'PUT', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

export function useDeleteOptionGroup(
  id: string,
  pid: string,
): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gid) =>
      merchantApiFetch<void>(`${API}/products/${pid}/option-groups/${gid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

/* ---- Catálogo: Opcionais — itens (Fase 3b) ---- */

export function useCreateOption(
  id: string,
  pid: string,
  gid: string,
): UseMutationResult<OwnerOption, unknown, Omit<OwnerOption, 'id'>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      merchantApiFetch<OwnerOption>(`${API}/option-groups/${gid}/options`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

export function useUpdateOption(
  id: string,
  pid: string,
  gid: string,
): UseMutationResult<OwnerOption, unknown, { oid: string; body: Partial<OwnerOption> }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ oid, body }) =>
      merchantApiFetch<OwnerOption>(`${API}/option-groups/${gid}/options/${oid}`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

export function useDeleteOption(
  id: string,
  pid: string,
  gid: string,
): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (oid) =>
      merchantApiFetch<void>(`${API}/option-groups/${gid}/options/${oid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.product(id, pid) });
    },
  });
}

/* ---- Alérgenos públicos (Fase 3b) ---- */

/**
 * Shape cru de `GET /public/allergens` (= AllergenResponse do backend).
 * O rótulo exibível vem em `label` (não `name`); `code` é o slug interno.
 */
interface RawAllergen {
  id: string;
  label?: string;
  name?: string;
  code?: string;
}

export function useAllergens(): UseQueryResult<OwnerAllergen[]> {
  return useQuery({
    queryKey: ['owner', 'allergens'],
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const rows = await merchantApiFetch<RawAllergen[]>(`${API}/public/allergens`);
      return rows.map((a) => ({ id: a.id, name: a.label ?? a.name ?? a.code ?? '' }));
    },
  });
}

/* ---- Operação do dia no Resumo (F5.6) ---- */

/** `MerchantDashboardStatsResponse.java` — agregação do DIA (cache por loja+data no backend). */
export interface OwnerDayStats {
  date: string;
  ordersCount: number;
  revenue: number;
  averageTicket: number;
  inPreparation: number;
  cancelled: number;
}

interface RawDayStats {
  date: string;
  ordersCount: number;
  revenue: unknown;
  averageTicket: unknown;
  inPreparation: number;
  cancelled: number;
}

const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export function useOwnerDayStats(id: string): UseQueryResult<OwnerDayStats> {
  return useQuery<OwnerDayStats>({
    queryKey: ['owner', 'day-stats', id],
    queryFn: async () => {
      const raw = await merchantApiFetch<RawDayStats>(`${API}/merchants/${id}/dashboard/stats`);
      return { ...raw, revenue: num(raw.revenue), averageTicket: num(raw.averageTicket) };
    },
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
}

/** Linha compacta de `GET /merchants/{id}/dashboard/recent-orders` (OrderResponse — só o que a lista usa). */
export interface OwnerRecentOrder {
  id: string;
  status: string;
  totalAmount: number;
  customerName?: string | null;
  createdAt: string;
  /** `OrderResponse.displayCode` (RF-COD-05); null em pedido pré-backfill → `orderDisplayCode`. */
  displayCode?: string | null;
}

export function useOwnerRecentOrders(id: string, limit = 5): UseQueryResult<OwnerRecentOrder[]> {
  return useQuery<OwnerRecentOrder[]>({
    queryKey: ['owner', 'recent-orders', id, limit],
    queryFn: async () => {
      const rows = await merchantApiFetch<Array<OwnerRecentOrder & { totalAmount: unknown }>>(
        `${API}/merchants/${id}/dashboard/recent-orders?limit=${limit}`,
      );
      return rows.map((r) => ({ ...r, totalAmount: num(r.totalAmount) }));
    },
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
}

/* ---- Billing (FAV-148) + configurações operacionais + capa (FAV-147) ---- */

/**
 * Razão social, CNPJ e chave PIX (decriptada) do dono —
 * `GET /merchants/{id}/billing-info`, guardado por canAccessMerchant.
 */
export function useBillingInfo(id: string): UseQueryResult<BillingInfo> {
  return useQuery<BillingInfo>({
    queryKey: ['owner', 'billing', id],
    queryFn: () => merchantApiFetch<BillingInfo>(`${API}/merchants/${id}/billing-info`),
    enabled: Boolean(id),
  });
}

/**
 * Atualização parcial da loja (PUT lazy-update): configurações operacionais
 * (pedido mínimo, tempo de preparo, auto-aceitar, dispatch) e a capa (FAV-147).
 */
export function useUpdateOwnerMerchant(
  id: string,
): UseMutationResult<OwnerMerchant, unknown, OwnerMerchantUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      merchantApiFetch<OwnerMerchant>(`${API}/merchants/${id}`, { method: 'PUT', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerKeys.merchant(id) });
    },
  });
}

/* ---- Financeiro do dono (P3 — backend WS-1..4 já pronto) ---- */

/** `MerchantFinanceDashboardResponse.java` — valores em CENTAVOS (MF-9). */
export interface OwnerFinanceDashboard {
  pendingBalanceCents: number;
  processingCents: number;
  nextPayoutDate?: string | null;
  receivedThisMonthCents: number;
  openDebtCents: number;
  commissionRateSubtotal: number;
  commissionRateDelivery: number;
  pixKeyMasked?: string | null;
  alerts: string[];
}

export function useFinanceDashboard(): UseQueryResult<OwnerFinanceDashboard> {
  return useQuery<OwnerFinanceDashboard>({
    queryKey: ['owner', 'finance', 'dashboard'],
    queryFn: async () => {
      const raw = await merchantApiFetch<Record<string, unknown>>(
        `${API}/merchants/me/finance/dashboard`,
      );
      return {
        pendingBalanceCents: num(raw.pendingBalanceCents),
        processingCents: num(raw.processingCents),
        nextPayoutDate: (raw.nextPayoutDate as string | null | undefined) ?? null,
        receivedThisMonthCents: num(raw.receivedThisMonthCents),
        openDebtCents: num(raw.openDebtCents),
        commissionRateSubtotal: num(raw.commissionRateSubtotal),
        commissionRateDelivery: num(raw.commissionRateDelivery),
        pixKeyMasked: (raw.pixKeyMasked as string | null | undefined) ?? null,
        alerts: Array.isArray(raw.alerts) ? (raw.alerts as string[]) : [],
      };
    },
    retry: false, // 409 MULTIPLE_MERCHANTS não se resolve tentando de novo
  });
}

/** `FinancialTransactionResponse.java` — repasses PIX-OUT (centavos). */
export interface OwnerFinanceTransaction {
  id: string;
  amountCents: number;
  status: string;
  pixKeyMasked?: string | null;
  createdAt: string;
  executedAt?: string | null;
}

export interface OwnerFinanceTransactionsPage {
  content: OwnerFinanceTransaction[];
  page: number;
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
}

export function useFinanceTransactions(page = 0): UseQueryResult<OwnerFinanceTransactionsPage> {
  return useQuery<OwnerFinanceTransactionsPage>({
    queryKey: ['owner', 'finance', 'transactions', page],
    queryFn: async () => {
      const raw = await merchantApiFetch<OwnerFinanceTransactionsPage>(
        `${API}/merchants/me/finance/transactions?page=${page}&size=20`,
      );
      return { ...raw, content: raw.content.map((t) => ({ ...t, amountCents: num(t.amountCents) })) };
    },
    retry: false,
  });
}

/* ---- Avaliações da loja (lista pública — nomes mascarados) ---- */

export interface OwnerReview {
  id: string;
  customerName?: string | null;
  rating: number;
  comment?: string | null;
  createdAt: string;
}

export function useOwnerReviews(id: string, size = 5): UseQueryResult<OwnerReview[]> {
  return useQuery<OwnerReview[]>({
    queryKey: ['owner', 'reviews', id, size],
    queryFn: async () => {
      const page = await merchantApiFetch<{ content: OwnerReview[] }>(
        `${API}/public/merchants/${id}/reviews?size=${size}`,
      );
      return page.content ?? [];
    },
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
}

/* ---- Ativação da loja (FAV-112, caminho tempPassword) ---- */

/**
 * Dono autenticado ativa a loja PENDING_OWNER_ACTIVATION aceitando os termos
 * (`POST /merchant-onboarding/activate`). Com auto-approve ligado (default) a
 * loja sai APPROVED na hora. Invalida as memberships pra `useResolveMerchant`
 * re-renderizar o resumo já com a loja ativa. Endpoint ausente do api.d.ts
 * stale → hand-modeled, como o resto deste arquivo.
 */
export function useActivateStore(): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (merchantId) =>
      merchantApiFetch<void>(`${API}/merchant-onboarding/activate`, {
        method: 'POST',
        body: { merchantId, acceptedTerms: true },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kds', 'my-merchants'] });
    },
  });
}

/* ---- Papel do usuário na loja (OWNER-gate de UI; backend é a fronteira real) ---- */

/**
 * Compartilha a queryKey `['kds','my-merchants']` com `useResolveMerchant` —
 * na prática não dispara fetch extra (já está no cache do shell). Usado para
 * esconder/desabilitar ações OWNER-only (antecipação, frota própria).
 */
export function useIsMerchantOwner(id: string): boolean {
  const { data } = useQuery({
    queryKey: ['kds', 'my-merchants'],
    queryFn: getMyMerchants,
    staleTime: 60_000,
  });
  return (data ?? []).some((m) => m.id === id && m.memberRole === 'OWNER');
}

/* ---- Antecipação de repasses (BACK-152 / MER-19) ---- */

export interface AnticipationOption {
  days: number;
  feePercent: number;
}

export interface AnticipationOptions {
  merchantId: string;
  overridden: boolean;
  chosenDays: number | null;
  options: AnticipationOption[];
}

export function useAnticipationOptions(id: string): UseQueryResult<AnticipationOptions> {
  return useQuery<AnticipationOptions>({
    queryKey: ['owner', 'anticipation', id],
    queryFn: () => merchantApiFetch<AnticipationOptions>(`${API}/merchants/${id}/anticipation-options`),
    enabled: Boolean(id),
  });
}

/** OWNER-only no backend (`@merchantSecurity.isOwner`) — a UI deve gatar via `useIsMerchantOwner`. */
export function useChooseAnticipation(
  id: string,
): UseMutationResult<AnticipationOptions, unknown, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      merchantApiFetch<AnticipationOptions>(`${API}/merchants/${id}/anticipation-choice`, {
        method: 'PUT',
        body: { days },
      }),
    onSuccess: (data) => {
      qc.setQueryData(['owner', 'anticipation', id], data);
    },
  });
}

/* ---- Frota própria — pool 2 (BACK-157 / MER-20) ---- */

export type CourierPoolStatus = 'INVITED' | 'ACTIVE' | 'REMOVED';

export interface CourierPoolEntry {
  id: string;
  merchantId: string;
  merchantName: string;
  courierId: string;
  courierName: string;
  courierPhone: string | null;
  status: CourierPoolStatus;
  invitedAt: string;
  respondedAt: string | null;
}

/** OWNER-only no backend — a UI deve gatar via `useIsMerchantOwner` (evita 403 desnecessário). */
export function useCourierPool(id: string, enabled = true): UseQueryResult<CourierPoolEntry[]> {
  return useQuery<CourierPoolEntry[]>({
    queryKey: ['owner', 'courier-pool', id],
    queryFn: () => merchantApiFetch<CourierPoolEntry[]>(`${API}/merchants/${id}/courier-pool`),
    enabled: Boolean(id) && enabled,
  });
}

export function useInviteCourier(
  id: string,
): UseMutationResult<CourierPoolEntry, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      merchantApiFetch<CourierPoolEntry>(`${API}/merchants/${id}/courier-pool`, {
        method: 'POST',
        body: { email },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['owner', 'courier-pool', id] });
    },
  });
}

export function useRemoveCourier(id: string): UseMutationResult<void, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courierId: string) =>
      merchantApiFetch<void>(`${API}/merchants/${id}/courier-pool/${courierId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['owner', 'courier-pool', id] });
    },
  });
}

/* ---- Avaliações — visão completa do dono (BACK-159 / MER-22) ---- */

export interface MerchantReviewEntry {
  id: string;
  orderId: string;
  targetType: 'MERCHANT' | 'COURIER';
  targetId: string;
  customerName: string | null;
  rating: number;
  comment: string | null;
  status: string;
  createdAt: string;
  reply: string | null;
  repliedAt: string | null;
}

export interface MerchantReviewsPage {
  content: MerchantReviewEntry[];
  page: number;
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
}

export function useOwnerAllReviews(id: string, page = 0): UseQueryResult<MerchantReviewsPage> {
  return useQuery<MerchantReviewsPage>({
    queryKey: ['owner', 'reviews-all', id, page],
    queryFn: () =>
      merchantApiFetch<MerchantReviewsPage>(`${API}/merchants/${id}/reviews/all?page=${page}&size=20`),
    enabled: Boolean(id),
  });
}

export function useReplyReview(
  id: string,
): UseMutationResult<MerchantReviewEntry, unknown, { reviewId: string; reply: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, reply }) =>
      merchantApiFetch<MerchantReviewEntry>(`${API}/merchants/${id}/reviews/${reviewId}/reply`, {
        method: 'PUT',
        body: { reply },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['owner', 'reviews-all', id] });
    },
  });
}

export interface OwnerReviewSummary {
  average: number;
  total: number;
  distribution: Record<number, number>;
}

export function useReviewSummary(id: string): UseQueryResult<OwnerReviewSummary> {
  return useQuery<OwnerReviewSummary>({
    queryKey: ['owner', 'reviews-summary', id],
    queryFn: () => merchantApiFetch<OwnerReviewSummary>(`${API}/public/merchants/${id}/reviews/summary`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}
